import React from 'react';
import { Grid, Box, Typography } from '@mui/material';
import { LabeledCheckbox } from './ui/labeled-checkbox';
import { useTranslation } from 'react-i18next';
import { HealthSystemDomainAnimalsResultClimateControlSearchQuery } from '../api/HealthSystemApi';
import { Slider } from './ui/slider';

const marksMap = {
  temperatureDelta: [
    { value: 0, label: '0°C' },
    { value: 30, label: '30°C' }
  ],
  humidity: [
    { value: 0, label: '0%' },
    { value: 100, label: '100%' }
  ],
  amonia: [
    { value: 0, label: '0ppm' },
    { value: 60, label: '60ppm' }
  ],
  carbon: [
    { value: 0, label: '0ppm' },
    { value: 5000, label: '5000ppm' }
  ]
};

const defaultRanges = {
  temperatureDelta: [0, 30],
  humidity: [0, 100],
  amonia: [0, 60],
  carbon: [0, 5000]
};

interface IProps {
  disabled?: boolean;
  onChange: (option: HealthSystemDomainAnimalsResultClimateControlSearchQuery) => void;
  value?: HealthSystemDomainAnimalsResultClimateControlSearchQuery;
}

const ClimateControlFilter = (props: IProps) => {
  const { disabled, onChange, value } = props;
  const { t } = useTranslation();

  const [ranges, setRanges] = React.useState(defaultRanges);

  const [enabledFilters, setEnabledFilters] = React.useState({
    temperatureDelta: false,
    humidity: false,
    amonia: false,
    carbon: false
  });

  // Sync from parent value
  React.useEffect(() => {
    if (!value) {
      return;
    }

    setRanges((prev) => ({
      temperatureDelta: value.temperatureDelta?.length === 2 ? value.temperatureDelta : prev.temperatureDelta,
      humidity: value.humidity?.length === 2 ? value.humidity : prev.humidity,
      amonia: value.amonia?.length === 2 ? value.amonia : prev.amonia,
      carbon: value.carbon?.length === 2 ? value.carbon : prev.carbon
    }));

    setEnabledFilters({
      temperatureDelta: !!value.temperatureDelta?.length,
      humidity: !!value.humidity?.length,
      amonia: !!value.amonia?.length,
      carbon: !!value.carbon?.length
    });
  }, [value]);

  // Notify parent about any change
  const handleChange = (customRanges = ranges, customEnabled = enabledFilters) => {
    const result: HealthSystemDomainAnimalsResultClimateControlSearchQuery = {
      temperatureDelta: customEnabled.temperatureDelta ? customRanges.temperatureDelta : null,
      humidity: customEnabled.humidity ? customRanges.humidity : null,
      amonia: customEnabled.amonia ? customRanges.amonia : null,
      carbon: customEnabled.carbon ? customRanges.carbon : null
    };

    onChange(result);
  };

  // onChange is called from the event handler (never inside a setState updater,
  // which would update the parent during render).
  const handleCheckboxChange = (key: keyof typeof enabledFilters) => {
    const updated = { ...enabledFilters, [key]: !enabledFilters[key] };
    setEnabledFilters(updated);
    handleChange(ranges, updated);
  };

  const handleRangeChange = (key: keyof typeof ranges, newValue: number[]) => {
    const updated = { ...ranges, [key]: newValue };
    setRanges(updated);
    handleChange(updated, enabledFilters);
  };

  const filters: {
    key: keyof typeof ranges;
    label: string;
  }[] = [
    { key: 'temperatureDelta', label: 'temperature-delta-range' },
    { key: 'humidity', label: 'humidity-range' },
    { key: 'amonia', label: 'amonia-range' },
    { key: 'carbon', label: 'carbon-range' }
  ];

  return (
    <Grid container spacing={1} sx={{ justifyContent: 'space-between' }}>
      {filters.map(({ key, label }) => (
        <Grid size={{ xs: 12, sm: 6 }} key={key} sx={{ px: 1 }}>
          <div style={{ minHeight: 40 }}>
            <LabeledCheckbox
              checked={enabledFilters[key]}
              onCheckedChange={() => handleCheckboxChange(key)}
              label={t(`f-search-${label}`)}
              disabled={disabled}
            />
          </div>
          <Box sx={{ pr: 1, pt: 0, pb: 0.5, pl: 1 }}>
            <Slider
              value={ranges[key]}
              disabled={disabled || !enabledFilters[key]}
              onValueChange={(newValue) => handleRangeChange(key, newValue)}
              min={defaultRanges[key][0]}
              max={defaultRanges[key][1]}
              showValueLabel
              aria-label={t(`f-search-${label}`)}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography
                variant="body1"
                sx={{ color: disabled || !enabledFilters[key] ? 'text.disabled' : 'text.primary' }}
              >
                {marksMap[key][0].label}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: disabled || !enabledFilters[key] ? 'text.disabled' : 'text.primary' }}
              >
                {marksMap[key][1].label}
              </Typography>
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

export default ClimateControlFilter;
